import { Spacing } from '@persona-id/design-tokens';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import InquiryInspectRunTree, {
  ActiveNodeElement,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryInspect/InquiryInspectRunTree';
import { PREVIEW_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import LoadingModal from 'dashboard/components/LoadingModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavBreadcrumb from 'dashboard/components-lib/NavBreadcrumb';
import NullState from 'dashboard/components-lib/NullState';
import {
  Backdrop,
  FullScreenHeaderContainer,
  StickyContainer,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionSchema,
  InquiryTemplateVersionStatus,
} from 'dashboard/models/inquiry-template-version';
import { WorkflowSchema } from 'dashboard/models/workflow-schema';
import { setInquiry } from 'dashboard/redux/slices/inquiry-inspect';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { getRootDomain } from 'lib/utils/url';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import { FromWidgetEvent } from 'verify/lib/clients/HolderClient';

const StyledStickyContainer = styled(StickyContainer)`
  // Lower the z-index so that a tooltip can render over it
  z-index: ${Z_INDICES.loadingOverlays};
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: -${Spacing[2]};
  margin-right: ${Spacing[4]};
  overflow: hidden;
`;

const HeaderButton = styled(Button).attrs(() => ({ width: 'auto' }))``;

const InspectContainer = styled.div`
  display: flex;
  flex-flow: column;
  height: 100vh;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  height: 100%;
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
`;

const InlineFlowScreenParentContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: #505060;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  overflow: auto;
  align-items: center;
`;

const InlineFlowScreenContainer = styled.div`
  width: ${PREVIEW_WIDTH}px;
  min-height: ${FRAME_HEIGHT}px;
  margin: auto;
`;

const generateIframeSrc = (host: string, inquiryId: string, messageTargetOrigin: string) => {
  return `${host}/widget?environment=sandbox&inquiry-id=${inquiryId}&iframe-origin=${host}&flow-type=inline&sandbox-tools-disabled=true
    &sandbox-banner-disabled=true&message-target-origin=${messageTargetOrigin}`;
};

const IframeContainer = styled.div`
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: auto;
`;

interface InspectIframeProps {
  inquiryId: string;
  handleOnStepChange: (stepName: string) => void;
}

const InspectIframe = ({ inquiryId, handleOnStepChange }: InspectIframeProps) => {
  // Specifies the intended target of the messages emitted by the widget, which is Dashboard.
  // Otherwise, the default target origin would be the origin of the widget, which is not the same as that of
  // Dashboard if the org has a subdomain
  const messageTargetOrigin = location.origin;

  const rootDomain = getRootDomain(location.host);
  const subdomain = rootDomain.includes('localhost') ? null : 'inquiry';

  const host = `${location.protocol}//${[subdomain, rootDomain].filter(Boolean).join('.')}`;
  const iframeSrc = generateIframeSrc(host, inquiryId, messageTargetOrigin);

  useEffect(() => {
    const handlePageChangeEvent = (e: MessageEvent) => {
      if (e.origin === host) {
        const data = e.data;
        if (data && typeof data === 'object' && data.name === FromWidgetEvent.PageChange) {
          handleOnStepChange(data.metadata.name);
        }
      }
    };

    window.addEventListener('message', handlePageChangeEvent);

    return () => {
      window.removeEventListener('message', handlePageChangeEvent);
    };
  }, []);

  return (
    // This is a workaround to make an apparently non-resizing hosted flow, similar to
    // the "embedded flow" in FLE Preview. Under the hood, we use an inline flow that takes
    // up the entire container specified for the widget dimensions (using the default dimensions).
    // Then we put that on top of the full height and width background typically used for hosted flow,
    // to make this integration look like a hosted flow that does not resize for mobile.
    <InlineFlowScreenParentContainer>
      <InlineFlowScreenContainer>
        <IframeContainer>
          <iframe
            style={{ position: 'relative', width: '100%', height: '100%' }}
            allow='camera; microphone'
            sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation'
            src={iframeSrc}
          ></iframe>
        </IframeContainer>
      </InlineFlowScreenContainer>
    </InlineFlowScreenParentContainer>
  );
};

interface InspectWidgetProps {
  inquiryId: string | undefined;
  isLoadingInquiry: boolean;
  handleOnStepChange: (stepName: string) => void;
}

const InspectWidget = ({ inquiryId, isLoadingInquiry, handleOnStepChange }: InspectWidgetProps) => {
  if (isLoadingInquiry) {
    return <Backdrop />;
  } else if (inquiryId == null) {
    return (
      <Backdrop>
        <NullState message='We couldn’t create an inquiry for inspection.' />
      </Backdrop>
    );
  }

  return <InspectIframe inquiryId={inquiryId} handleOnStepChange={handleOnStepChange} />;
};

export interface InquiryInspectTreeWrapperProps {
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  stepsPreview: StepPreview[] | undefined;
  schema: InquiryTemplateVersionSchema | undefined;
  workflowSchema: WorkflowSchema;
  loadingInquiryTemplateData: boolean;
  transitionStateRef: RefObject<{
    currentStepName?: string;
    previousStepName?: string;
  }>;
  numTransitions: number;
}

export interface InquiryInspectProps {
  inquiry: Inquiry | null;
  inquiryTemplateVersion: InquiryTemplateVersion | null;
  inquiryTemplateVersionSchema: InquiryTemplateVersionSchema | null;
  inquiryTemplateVersionStepsPreview: StepPreview[] | null;
  inquiryTemplate: InquiryTemplate | null;
  referenceId?: string;
  workflowSchema: WorkflowSchema;
  isLoadingInquiry: boolean;
  loadingInquiryTemplateData: boolean;
  handleCreateInquiry: (props: {
    referenceId?: string;
    accountTypeId?: string;
    fields?: Record<string, any>;
  }) => Promise<void>;
}

export const InquiryInspect = ({
  inquiryTemplateVersion,
  inquiryTemplateVersionSchema,
  inquiryTemplateVersionStepsPreview,
  inquiryTemplate,
  inquiry,
  referenceId,
  workflowSchema,
  isLoadingInquiry,
  loadingInquiryTemplateData,
  handleCreateInquiry,
}: InquiryInspectProps) => {
  const transitionStateRef = useRef<{
    currentStepName?: string;
    previousStepName?: string;
  }>({});
  // Stores DOM references to the previous visual step, current visual step,
  // and any associated connector. These elements are used to find the appropriate
  // bounding box for "active" elements. All workflow nodes are guaranteed to be
  // within the bounding box for those three items
  const activeNodeElementsRef = useRef<
    Partial<Record<ActiveNodeElement, { id: string; element: HTMLDivElement }>>
  >({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Because we use a ref to track the current and previous steps for the purpose
  // of keeping those values fresh for callbacks, we need another manner by which
  // to trigger rerender of child components when the ref values change. For
  // every step change we increment the transition count.
  const [numTransitions, setNumTransitions] = useState(0);

  const updateTransition = (newStepName: string) => {
    transitionStateRef.current = {
      previousStepName: transitionStateRef.current.currentStepName,
      currentStepName: newStepName,
    };
    setNumTransitions((n) => n + 1);
  };

  useEffect(() => {
    if (inquiry == null) {
      handleCreateInquiry({ referenceId: referenceId });
    }
  }, [handleCreateInquiry, inquiry]);

  if (loadingInquiryTemplateData) {
    return (
      <InspectContainer>
        <ContentContainer>
          <LoadingIndicator />
        </ContentContainer>
      </InspectContainer>
    );
  }

  if (inquiryTemplateVersion?.attributes.status === InquiryTemplateVersionStatus.Draft) {
    return (
      <Backdrop>
        <NullState message='Inspect is available only for published versions.' />
      </Backdrop>
    );
  }

  if (
    inquiryTemplateVersion == null ||
    inquiryTemplateVersionStepsPreview == null ||
    inquiryTemplateVersionSchema == null ||
    inquiryTemplate == null
  ) {
    return (
      <Backdrop>
        <NullState message='We couldn’t load the template data for inspect.' />
      </Backdrop>
    );
  }

  return (
    <InspectContainer>
      <StyledStickyContainer>
        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBreadcrumb
              name={inquiryTemplate.attributes.name}
              breadcrumbItems={[
                {
                  displayName: 'Inspect' + (inquiry ? ` (${inquiry.id})` : ''),
                },
              ]}
              onBackClick={() => {
                navigate(
                  `/dashboard/inquiry-templates/${inquiryTemplate.id}/${inquiryTemplateVersion.id}/flow`,
                );
              }}
            />
          </HorizontalContainer>
          <HStack spacing={Spacing[3]}>
            <HeaderButton
              variant='basic'
              disabled={inquiry == null}
              onClick={() => {
                if (inquiry == null) {
                  return;
                }
                window.open(`/dashboard/inquiries/${inquiry.id}`, '_blank');
              }}
            >
              View Inquiry
            </HeaderButton>
            <HeaderButton
              variant='basic'
              onClick={() => {
                transitionStateRef.current = {};
                activeNodeElementsRef.current = {};
                dispatch(setInquiry({ inquiry: null }));
              }}
            >
              Restart
            </HeaderButton>
          </HStack>
        </FullScreenHeaderContainer>
      </StyledStickyContainer>
      <LoadingModal title='Loading...' isLoading={isLoadingInquiry} />
      <ContentContainer>
        <InspectWidget
          inquiryId={inquiry?.id}
          isLoadingInquiry={isLoadingInquiry}
          handleOnStepChange={(stepName) => {
            updateTransition(stepName);
          }}
        />
        <InquiryInspectRunTree
          inquiryTemplateVersion={inquiryTemplateVersion}
          transitionStateRef={transitionStateRef}
          activeNodeElementsRef={activeNodeElementsRef}
          numTransitions={numTransitions}
          workflowSchema={workflowSchema}
          stepsPreview={inquiryTemplateVersionStepsPreview}
          schema={inquiryTemplateVersionSchema}
        />
      </ContentContainer>
    </InspectContainer>
  );
};
